/**
 * Login/auth page configuration.
 *
 * `AUTH_ACCENT_HUE` is the single knob that drives every accent colour
 * on the auth surface — the gradient sign-in button, the centerline
 * canvas glow, the feature-card hover tint, etc. It is a hue in OKLCH
 * space (0–360):
 *   - 200 → cyan-teal (matches the app's `--primary`)
 *   - 230 → blue
 *   - 160 → green
 *   - 320 → magenta
 *
 * Change this number to re-tint the whole login experience.
 */
export const AUTH_ACCENT_HUE = 200;

export const AUTH_COPY = {
    eyebrow: 'Cloud platform · v2.4',
    headline: 'Plan endovascular procedures with confidence.',
    subheadline:
        'Automated 3D segmentation, centerline navigation and measurement — directly in your browser.',
} as const;

export type AuthFeatureIcon = 'segmentation' | 'centerline' | 'measure';

export interface AuthFeature {
    code: string;
    title: string;
    body: string;
    icon: AuthFeatureIcon;
}

export const AUTH_FEATURES: readonly AuthFeature[] = [
    {
        code: '01 · SEG',
        title: '3D Segmentation',
        body: 'Automated arterial tree extraction from CTA in under one minute. Editable, reproducible, audit-trailed.',
        icon: 'segmentation',
    },
    {
        code: '02 · NAV',
        title: 'Centerline Navigation',
        body: 'Curved planar reformat along the vessel centerline with smooth orthogonal cross-sections.',
        icon: 'centerline',
    },
    {
        code: '03 · MES',
        title: 'Measurement',
        body: 'Diameter, length and angle measurements with sub-millimetre precision, export-ready for sizing reports.',
        icon: 'measure',
    },
] as const;

/**
 * Build an `oklch(...)` string for the configured accent hue.
 * Used by inline styles that need a hue-driven colour (canvas, button
 * gradient) — anything that can be expressed in Tailwind tokens should
 * use those instead.
 */
export function accentColor(l: number, c: number, alpha?: number, hue: number = AUTH_ACCENT_HUE) {
    const base = `${l}% ${c} ${hue}`;
    return alpha === undefined ? `oklch(${base})` : `oklch(${base} / ${alpha})`;
}
